import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useConvexAuth, useQuery, useMutation, useConvex } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

// Types
export type AdminRole =
  | 'supreme_admin'
  | 'principal'
  | 'school_admin_office'
  | 'admin'
  | 'event_manager'
  | 'escort';

export interface School {
  _id: Id<'schools'>;
  school_id?: Id<'schools'>;  // Alias for _id, set on client
  school_name: string;
  branch_name?: string;
  access_key: string;
  team_code: string;
  principal_name?: string;
  principal_contact?: string;
  address?: string;
  email?: string;
  is_active: boolean;
  created_at: number;
  created_by: string;
  total_participants?: number;
}

interface User {
  id: string;
  email: string | null;
  name: string | null;
  imageUrl: string | null;
}

interface AuthContextType {
  user: User | null;
  school: School | null;
  loading: boolean;
  login: (accessKey: string) => Promise<void>;
  loginAdmin: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  adminRole: AdminRole | null;
  assignedEvents: string[];
  assignedSchoolId: string | null;
  isMainAdmin: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session ID for anonymous school logins
const SESSION_STORAGE_KEY = 'enigma_school_session';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { isLoading: convexAuthLoading, isAuthenticated: isConvexAuthenticated } = useConvexAuth();
  const { signIn, signOut: convexSignOut } = useAuthActions();
  const convex = useConvex();

  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [isSchoolLogin, setIsSchoolLogin] = useState(false);

  // Get the current Convex Auth user (email, name, image from Google OAuth)
  const currentUser = useQuery(
    api.users.viewer,
    isConvexAuthenticated ? {} : 'skip'
  );

  // Get admin profile using email from Convex Auth user
  const adminProfile = useQuery(
    api.adminAuth.getAdminProfile,
    currentUser?.email
      ? { email: currentUser.email }
      : 'skip'
  );

  // School session query
  const schoolByUserId = useQuery(
    api.adminAuth.getSchoolByUserId,
    sessionUserId ? { userId: sessionUserId } : 'skip'
  );

  // Convex mutations
  const createSession = useMutation(api.adminAuth.createUserSchoolSession);
  const clearSessionMutation = useMutation(api.adminAuth.clearUserSession);
  const logSchoolLoginMutation = useMutation(api.logging.logSchoolLogin);
  const ensureMainAdminMutation = useMutation(api.adminAuth.ensureMainAdmin);

  // Check for existing school session on mount
  useEffect(() => {
    const storedSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        setSessionUserId(session.userId);
        setIsSchoolLogin(true);
      } catch {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
    setLoading(false);
  }, []);

  // Update school from Convex query result
  useEffect(() => {
    if (schoolByUserId && isSchoolLogin) {
      const schoolData = schoolByUserId as { _id: Id<'schools'> } & Omit<School, '_id' | 'school_id'>;
      setSchool({ ...schoolData, school_id: schoolData._id } as School);
    }
  }, [schoolByUserId, isSchoolLogin]);

  // Ensure main admin exists when they log in
  useEffect(() => {
    const email = currentUser?.email;
    if (email && email === import.meta.env.VITE_ADMIN_EMAIL) {
      ensureMainAdminMutation({ email });
    }
  }, [currentUser, ensureMainAdminMutation]);

  // Derive auth state
  const mainAdminEmail = import.meta.env.VITE_ADMIN_EMAIL;
  const userEmail = currentUser?.email;

  const isAdmin = Boolean(
    userEmail && (adminProfile?.is_active || userEmail === mainAdminEmail)
  );

  const adminRole: AdminRole | null = isAdmin
    ? (adminProfile?.role as AdminRole) ||
      (userEmail === mainAdminEmail ? 'supreme_admin' : 'admin')
    : null;

  const isMainAdmin = adminRole === 'supreme_admin';
  const assignedEvents = (adminProfile?.assigned_events as string[]) || [];
  const assignedSchoolId = (adminProfile?.assigned_school_id as string) || null;

  // Login with access key (for schools)
  const login = useCallback(async (accessKey: string) => {
    setLoading(true);
    try {
      const result = await convex.query(api.adminAuth.loginWithAccessKey, { accessKey });

      if (!result.success || !result.school) {
        throw new Error(result.error || 'Invalid access key');
      }

      // Generate a session ID for the anonymous school login
      const userId = `school_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store session in browser
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
        userId,
        schoolId: result.school._id,
        loginTime: Date.now(),
      }));

      // Create session in Convex
      await createSession({
        userId,
        schoolId: result.school._id as Id<'schools'>,
      });

      // Log the login
      await logSchoolLoginMutation({
        schoolId: result.school._id,
        schoolName: result.school.school_name,
        success: true,
      });

      setSessionUserId(userId);
      setSchool({ ...result.school, school_id: result.school._id } as School);
      setIsSchoolLogin(true);
    } catch (error) {
      await logSchoolLoginMutation({
        schoolId: 'unknown',
        schoolName: accessKey,
        success: false,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [convex, createSession, logSchoolLoginMutation]);

  // Login as admin (using Convex Auth Google OAuth)
  const loginAdmin = useCallback(async () => {
    await signIn('google');
  }, [signIn]);

  // Logout
  const logout = useCallback(async () => {
    if (isSchoolLogin && sessionUserId) {
      await clearSessionMutation({ userId: sessionUserId });
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      setSessionUserId(null);
      setSchool(null);
      setIsSchoolLogin(false);
    }

    if (isConvexAuthenticated) {
      await convexSignOut();
    }
  }, [isSchoolLogin, sessionUserId, isConvexAuthenticated, convexSignOut, clearSessionMutation]);

  // Build user object for context
  const user: User | null = currentUser
    ? {
        id: currentUser._id,
        email: currentUser.email || null,
        name: currentUser.name || null,
        imageUrl: currentUser.image || null,
      }
    : isSchoolLogin && sessionUserId
    ? {
        id: sessionUserId,
        email: null,
        name: school?.school_name || null,
        imageUrl: null,
      }
    : null;

  const isAuthenticated = Boolean(isConvexAuthenticated || (isSchoolLogin && school));

  return (
    <AuthContext.Provider
      value={{
        user,
        school,
        loading: loading || convexAuthLoading,
        login,
        loginAdmin,
        logout,
        isAdmin,
        adminRole,
        assignedEvents,
        assignedSchoolId,
        isMainAdmin,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
